import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"

const ProfileRenderContext = createContext(null)

// Context and provider for managing profileInfoMode overrides across the application.
export function ProfileRenderProvider({ children }) {
  // Holds the single active override for profileInfoMode.
  // Shape: { ownerId, profileInfoMode } | null
  const [overrideState, setOverrideState] = useState(null)

  const setProfileInfoModeOverride = useCallback((ownerId, profileInfoMode) => {
    if (!ownerId) return

    // Last writer wins by design; only one dynamic page should own override at a time.
    setOverrideState({ ownerId, profileInfoMode: profileInfoMode || null })
  }, [])

  const clearProfileInfoModeOverride = useCallback((ownerId) => {
    if (!ownerId) return

    // Owner-safe clear: do not clear override set by another mounted page.
    setOverrideState((prev) => (prev?.ownerId === ownerId ? null : prev))
  }, [])

  // Build the context value object that will be provided to consumers.
  const value = useMemo(
    () => ({
      overrideProfileInfoMode: overrideState?.profileInfoMode || null,
      setProfileInfoModeOverride,
      clearProfileInfoModeOverride,
    }),
    [overrideState, setProfileInfoModeOverride, clearProfileInfoModeOverride],
  )

  // Provide the context value to all child components.
  return (
    <ProfileRenderContext.Provider value={value}>
      {children}
    </ProfileRenderContext.Provider>
  )
}

// Custom hook to access the profile render context. Must be used within a ProfileRenderProvider.
export function useProfileRenderContext() {
  const context = useContext(ProfileRenderContext)

  if (!context) {
    throw new Error("useProfileRenderContext must be used within ProfileRenderProvider")
  }

  return context
}

// Custom hook to manage the profileInfoMode override for a component.
// Automatically sets and clears the override based on the provided profileInfoMode.
export function useProfileInfoModeOverride(profileInfoMode) {
  const { setProfileInfoModeOverride, clearProfileInfoModeOverride } =
    useProfileRenderContext()

  // Stable owner id for this hook instance; used to prevent cross-page cleanup races.
  const ownerIdRef = useRef(Symbol("profileInfoModeOwner"))

  useEffect(() => {
    const ownerId = ownerIdRef.current

    if (!profileInfoMode) {
      // Explicitly clear when caller disables override.
      clearProfileInfoModeOverride(ownerId)
      return () => {
        // Always cleanup on unmount as a safety net.
        clearProfileInfoModeOverride(ownerId)
      }
    }

    // When enabled, this page becomes the current override owner.
    setProfileInfoModeOverride(ownerId, profileInfoMode)

    return () => {
      // Cleanup only removes this owner's override.
      clearProfileInfoModeOverride(ownerId)
    }
  }, [
    profileInfoMode,
    setProfileInfoModeOverride,
    clearProfileInfoModeOverride,
  ])

  return ownerIdRef.current
}
